import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, initials, money } from "@/lib/format";
import { PageHeader, ErrorState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

import { UserPlus, Trash2, Shield, LogOut, Loader2, Mail, Users, Trophy, Boxes, RefreshCw, DatabaseBackup, Fingerprint, Layers, Ship } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function FlooringSyncCard() {
  const [teams, setTeams] = useState(null);
  const [saving, setSaving] = useState(false);
  const q = useQuery({ queryKey: ["flooring-settings"], queryFn: async () => (await client.get("/flooring/settings")).data });
  const value = teams ?? ((q.data?.portal_teams || []).join("\n"));

  const save = async () => {
    setSaving(true);
    try {
      const list = value.split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await client.put("/flooring/settings", { portal_teams: list });
      toast.success("Flooring job sync saved");
      setTeams(null);
      q.refetch();
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]" data-testid="flooring-sync-card">
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Layers className="h-4 w-4" /></div>
        <div>
          <h2 className="font-display text-base font-bold text-ink">Flooring Job Sync</h2>
          <p className="text-xs text-muted-foreground">Portal team names used when assigning a flooring job. An assigned team is only sent if it exactly matches one of these.</p>
        </div>
      </div>
      <Label className="mb-1.5 block text-xs">Portal team names — one per line</Label>
      <Textarea value={value} onChange={(e) => setTeams(e.target.value)} placeholder={"Team 1\nTeam 2\nInstallers North"} className="min-h-[96px] font-mono text-sm" data-testid="flooring-teams-input" />
      <div className="mt-3 flex justify-end">
        <Button onClick={save} disabled={saving || teams === null} data-testid="flooring-teams-save" className="gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Save</Button>
      </div>
    </div>
  );
}

function InviteDialog({ onInvited }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("rep");
  const [mode, setMode] = useState("shared");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSaving(true);
    try {
      const { data } = await client.post("/invites", { email, role, pipeline_mode: mode, app_url: window.location.origin });
      toast.success(data.email_sent ? `Invite emailed to ${email}` : `Invite created for ${email} (email couldn't be sent)`);
      setEmail(""); setRole("rep"); setMode("shared"); setOpen(false);
      onInvited?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="invite-btn"><UserPlus className="h-4 w-4" />Invite staff</Button>
      </DialogTrigger>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Invite a team member</DialogTitle><DialogDescription>They'll get access when they sign in with this Google email.</DialogDescription></DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid gap-1.5">
            <Label>Google email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@tilesmate.com.au" data-testid="invite-email-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rep">Sales rep</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                  <SelectItem value="estimator">Estimator (plans only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Pipeline access</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="invite-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Shared (all leads)</SelectItem>
                  <SelectItem value="individual">Own leads only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="invite-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomsAgentsCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["customs-agents"], queryFn: async () => (await client.get("/customs/agents")).data });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const rows = data || [];
  const portalUrl = `${window.location.origin}/customs`;
  const add = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setAdding(true);
    try { await client.post("/customs/agents", { email, name }); toast.success("Customs agent approved"); setEmail(""); setName(""); qc.invalidateQueries({ queryKey: ["customs-agents"] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); } finally { setAdding(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.email}? Their access ends immediately.`)) return;
    try { await client.delete(`/customs/agents/${a.id}`); toast.success("Access removed"); qc.invalidateQueries({ queryKey: ["customs-agents"] }); }
    catch { toast.error("Couldn't remove"); }
  };
  return (
    <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]" data-testid="customs-agents-card">
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Ship className="h-4 w-4" /></div>
        <div>
          <h2 className="font-display text-base font-bold text-ink">Customs portal access</h2>
          <p className="text-xs text-muted-foreground">Approved agents sign in with an email code to view all shipments (read-only). They can never see any CRM or customer data.</p>
        </div>
      </div>
      <div className="mb-4 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
        <span className="text-xs text-muted-foreground">Portal link:</span>
        <code className="flex-1 truncate text-xs font-semibold text-ink">{portalUrl}</code>
        <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link copied"); }} data-testid="customs-portal-copy"><Mail className="h-3 w-3" />Copy</Button>
      </div>
      <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="agent@customsbroker.com" data-testid="customs-agent-email" />
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" data-testid="customs-agent-name" />
        <Button onClick={add} disabled={adding} className="gap-1.5" data-testid="customs-agent-add"><UserPlus className="h-4 w-4" />Approve</Button>
      </div>
      {isLoading ? <div className="h-16 animate-pulse rounded-xl bg-muted/50" />
        : rows.length === 0 ? <p className="rounded-xl border border-dashed border-border bg-white/60 p-4 text-center text-xs text-muted-foreground">No customs agents yet. Add an approved email above.</p>
        : <div className="divide-y divide-slate-100" data-testid="customs-agent-list">
          {rows.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`customs-agent-${a.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-ink">{a.name || a.email}</div>
                <div className="truncate text-xs text-muted-foreground">{a.email}{a.last_login_at ? ` · last in ${new Date(a.last_login_at).toLocaleDateString()}` : " · never signed in"}</div>
              </div>
              <button onClick={() => remove(a)} data-testid={`customs-agent-remove-${a.id}`} className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-error"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>}
    </div>
  );
}

function CustomsAccessLogCard() {
  const { data, isLoading } = useQuery({ queryKey: ["customs-access-log"], queryFn: async () => (await client.get("/customs/access-log")).data });
  const rows = data || [];
  return (
    <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]" data-testid="customs-access-log-card">
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-[#0891B2]/10 text-[#0891B2]"><Ship className="h-4 w-4" /></div>
        <div><h2 className="font-display text-base font-bold text-ink">Customs access log</h2><p className="text-xs text-muted-foreground">Every shipment a customs agent opened or noted, most recent first.</p></div>
      </div>
      {isLoading ? <div className="h-16 animate-pulse rounded-xl bg-muted/50" />
        : rows.length === 0 ? <p className="rounded-xl border border-dashed border-border bg-white/60 p-4 text-center text-xs text-muted-foreground">No customs activity yet.</p>
        : <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto" data-testid="customs-access-log-list">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <div className="min-w-0"><div className="truncate font-semibold text-ink">{r.agent_name || r.agent_email}</div><div className="truncate text-xs text-muted-foreground">{r.action === "view" ? "Viewed" : r.action?.startsWith("note") ? `Note · ${r.action.split(":")[1]}` : r.action} · {r.vessel_ref || r.vessel_id}</div></div>
              <span className="shrink-0 text-[11px] text-muted-foreground">{new Date(r.at).toLocaleString()}</span>
            </div>
          ))}
        </div>}
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";

  const users = useQuery({ queryKey: ["users"], queryFn: async () => (await client.get("/users")).data, enabled: isAdmin });
  const invites = useQuery({ queryKey: ["invites"], queryFn: async () => (await client.get("/invites")).data, enabled: isAdmin });
  const board = useQuery({ queryKey: ["leaderboard"], queryFn: async () => (await client.get("/leaderboard")).data, enabled: isAdmin });
  const inflowStatus = useQuery({ queryKey: ["inflow-status"], queryFn: async () => (await client.get("/inflow/status")).data, enabled: isAdmin });
  const [syncing, setSyncing] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await client.get("/backup/export", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `tilesmate-backup-${Date.now()}.json`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally { setBackingUp(false); }
  };

  const syncProducts = async () => {
    setSyncing(true);
    try {
      const { data } = await client.post("/inflow/products/sync");
      toast.success(`Synced ${data.synced} inFlow products`);
      qc.invalidateQueries({ queryKey: ["inflow-status"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const updateUser = async (u, patch) => {
    try {
      await client.put(`/users/${u.user_id}`, patch);
      toast.success("Access updated");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const deleteInvite = async (email) => {
    try {
      await client.delete(`/invites/${encodeURIComponent(email)}`);
      toast.success("Invite removed");
      qc.invalidateQueries({ queryKey: ["invites"] });
    } catch { toast.error("Could not remove invite"); }
  };

  const resendInvite = async (email) => {
    try {
      const { data } = await client.post(`/invites/${encodeURIComponent(email)}/resend`, { app_url: window.location.origin });
      toast.success(data.email_sent ? `Invite re-sent to ${email}` : "Couldn't send email");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend invite"); }
  };

  const resetDeviceLock = async (u) => {
    if (!window.confirm(`Reset device lock for ${u.name}? They'll set up Face ID / fingerprint again next time they open the app on their phone.`)) return;
    try {
      await client.delete(`/webauthn/credentials/${u.user_id}`);
      toast.success("Device lock reset");
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset device lock"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email}) from the team? They'll lose access immediately. Their existing leads stay in the CRM.`)) return;
    try {
      await client.delete(`/users/${u.user_id}`);
      toast.success(`${u.name} removed from the team`);
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove user"); }
  };

  const pendingInvites = (invites.data || []).filter((i) => i.status !== "joined");

  return (
    <div className="animate-fade-up max-w-4xl">
      <PageHeader title="Settings" subtitle="Your account and team access" testid="settings-header" />

      {/* My account */}
      <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
        <div className="flex items-center gap-4">
          <Avatar className="h-14 w-14">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-accent text-accent-foreground font-semibold">{initials(user?.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-display text-lg font-bold text-ink" data-testid="settings-user-name">{user?.name}</div>
            <div className="text-sm text-muted-foreground">{user?.email}</div>
          </div>
          <Button variant="outline" onClick={logout} data-testid="logout-btn" className="gap-2 text-error hover:text-error"><LogOut className="h-4 w-4" />Log out</Button>
        </div>
        <div className="mt-5 flex flex-wrap gap-3 border-t border-border pt-4 text-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 font-medium text-accent-foreground capitalize"><Shield className="h-3.5 w-3.5" />{user?.role}</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 font-medium text-ink capitalize">{user?.pipeline_mode} pipeline</span>
          {user?.is_super_admin && <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">Super admin</span>}
        </div>
      </div>

      {!isAdmin ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/60 p-8 text-center">
          <Users className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Team management is available to admins. Ask your admin to change your access.</p>
        </div>
      ) : (
        <>
          {/* Rep leaderboard — won value this month */}
          <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]" data-testid="leaderboard-card">
            <div className="mb-4 flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-[#D97706]/12 text-warning"><Trophy className="h-4 w-4" /></div>
              <div>
                <h2 className="font-display text-base font-bold text-ink">Rep leaderboard</h2>
                <p className="text-xs text-muted-foreground">Won pipeline value · {board.data?.month || "this month"}</p>
              </div>
            </div>
            {board.isError ? (
              <ErrorState onRetry={() => board.refetch()} message="We couldn't load the leaderboard. Please try again." />
            ) : board.isLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />)}</div>
            ) : (board.data?.entries || []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No team members yet.</p>
            ) : (
              <div className="space-y-2">
                {board.data.entries.map((e, i) => (
                  <div key={e.user_id} data-testid={`leaderboard-row-${e.user_id}`} className="flex items-center gap-3 rounded-lg border border-border p-3">
                    <span className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold",
                      i === 0 ? "bg-[#D97706]/15 text-warning" : "bg-muted text-muted-foreground")}>{i + 1}</span>
                    <Avatar className="h-9 w-9"><AvatarImage src={e.picture} /><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(e.name)}</AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-ink">{e.name}</div>
                      <div className="text-xs text-muted-foreground">{e.won_count} won · {e.quotes_sent} quotes · {e.conversion}% conv</div>
                    </div>
                    <div className="font-display text-base font-extrabold text-success">{money(e.won_value)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* inFlow product catalogue */}
          <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]" data-testid="inflow-card">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-accent text-accent-foreground"><Boxes className="h-4 w-4" /></div>
                <div>
                  <h2 className="font-display text-base font-bold text-ink">inFlow product catalogue</h2>
                  <p className="text-xs text-muted-foreground" data-testid="inflow-status-text">
                    {inflowStatus.data?.enabled
                      ? (inflowStatus.data?.products_cached
                        ? `${inflowStatus.data.products_cached} products cached · synced ${fmtDate(inflowStatus.data.products_synced_at, "d MMM yyyy, h:mmaaa")}`
                        : "Connected — not synced yet")
                      : "Add INFLOW_API_KEY + INFLOW_COMPANY_ID in Secrets to enable"}
                  </p>
                </div>
              </div>
              <Button onClick={syncProducts} disabled={!inflowStatus.data?.enabled || syncing} variant="outline" data-testid="inflow-sync-btn" className="gap-2">
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now
              </Button>
            </div>
          </div>

          <FlooringSyncCard />
          <CustomsAgentsCard />
          <CustomsAccessLogCard />
          <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]" data-testid="backup-card">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><DatabaseBackup className="h-4 w-4" /></div>
                <div>
                  <h2 className="font-display text-base font-bold text-ink">Data backup</h2>
                  <p className="text-xs text-muted-foreground">Download a full JSON snapshot of every record (customers, enquiries, builders, jobs, audit log). Keep it safe off-platform.</p>
                </div>
              </div>
              <Button onClick={downloadBackup} disabled={backingUp} variant="outline" data-testid="backup-download-btn" className="gap-2">
                {backingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />} Download backup
              </Button>
            </div>
          </div>

          {/* Team members */}
          <div className="mb-6 rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-base font-bold text-ink">Team members</h2>
                <p className="text-xs text-muted-foreground">Manage roles and pipeline visibility</p>
              </div>
              <InviteDialog onInvited={() => { qc.invalidateQueries({ queryKey: ["invites"] }); }} />
            </div>
            <div className="space-y-2">
              {(users.data || []).map((u) => (
                <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="flex flex-col gap-3 rounded-lg border border-border p-3 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar className="h-9 w-9"><AvatarImage src={u.picture} /><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(u.name)}</AvatarFallback></Avatar>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 truncate text-sm font-semibold text-ink">{u.name}{u.is_super_admin && <span className="rounded bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">SUPER</span>}</div>
                      <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Select value={u.role || "rep"} onValueChange={(v) => updateUser(u, { role: v })} disabled={u.is_super_admin}>
                      <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rep">Sales rep</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="warehouse">Warehouse</SelectItem>
                        <SelectItem value="estimator">Estimator (plans only)</SelectItem>
                      </SelectContent>
                    </Select>
                    {u.role !== "warehouse" && (
                      <Select value={u.pipeline_mode} onValueChange={(v) => updateUser(u, { pipeline_mode: v })} disabled={u.is_super_admin}>
                        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`mode-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="shared">Shared pipeline</SelectItem>
                          <SelectItem value="individual">Own leads only</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {u.has_device_lock && (
                      <button
                        onClick={() => resetDeviceLock(u)}
                        data-testid={`reset-lock-${u.user_id}`}
                        title="Reset device lock (phone app)"
                        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-accent"
                      >
                        <Fingerprint className="h-3.5 w-3.5" /><span className="hidden sm:inline">Reset lock</span>
                      </button>
                    )}
                    {!u.is_super_admin && u.email !== user?.email && (
                      <button
                        onClick={() => deleteUser(u)}
                        data-testid={`delete-user-${u.user_id}`}
                        title="Remove from team"
                        className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-error/10 hover:text-error"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending invites */}
          <div className="rounded-2xl border border-border bg-white p-5 shadow-[0_2px_8px_rgba(35,52,90,0.04)]">
            <h2 className="mb-4 font-display text-base font-bold text-ink">Pending invites</h2>
            {pendingInvites.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No pending invites.</p>
            ) : (
              <div className="space-y-2">
                {pendingInvites.map((inv) => (
                  <div key={inv.email} data-testid={`invite-row-${inv.email}`} className="flex items-center gap-3 rounded-lg border border-border p-3">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground"><Mail className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-ink">{inv.email}</div>
                      <div className="text-xs text-muted-foreground capitalize">{inv.role} · {inv.pipeline_mode} · invited {fmtDate(inv.created_at, "d MMM")}</div>
                    </div>
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", "bg-[#D97706]/12 text-warning")}>pending</span>
                    <button onClick={() => resendInvite(inv.email)} data-testid={`resend-invite-${inv.email}`} className="rounded-md border border-border px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-accent">Resend</button>
                    <button onClick={() => deleteInvite(inv.email)} data-testid={`delete-invite-${inv.email}`} className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-error"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
